import React, { useState, useRef, useEffect, useMemo } from 'react';
import './App.css';

// --- API Configuration ---
const API_URL = 'https://searchapi.aceiq.ca/search';
// ACTION: Switched to a more reliable CORS proxy to fix the 403 Forbidden error.
const CORS_PROXY = 'https://api.allorigins.win/raw?url=';
const ACEIQ_LOGO_URL = 'https://aceiq.ca/_image?href=%2F_astro%2Flogo.BhcWGUVz.jpg&w=1080&h=1080&f=webp';

// --- Helper Component for Rendering Snippets ---
/**
 * A component that takes text and a query, finds the query terms in the text,
 * and renders the text with the terms highlighted.
 * @param {{ text: string, query: string }} props
 */
const Snippet = ({ text, query }) => {
  if (!query || !text) {
    return <span>{text}</span>;
  }

  const terms = query.split(/\s+/).filter(Boolean);
  const regex = new RegExp(`(${terms.join('|')})`, 'gi');
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
      )}
    </span>
  );
};


// --- Main App Component ---
function App() {
  // --- State Management ---
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [metadata, setMetadata] = useState({});

  const searchInputRef = useRef(null);

  // --- Effects ---
  useEffect(() => {
    searchInputRef.current.focus();
  }, []);

  useEffect(() => {
    if (results.length > 0) {
      fetchMetadataForResults(results, query);
    }
  }, [results, query]);

  // --- Data Fetching ---
  /**
   * Fetches metadata and generates a contextual snippet for the top search results.
   * @param {Array} searchResults - The list of search results.
   * @param {string} currentQuery - The user's search query.
   */
  const fetchMetadataForResults = async (searchResults, currentQuery) => {
    const newMetadata = { ...metadata };
    const resultsToProcess = searchResults.slice(0, 5);

    for (const result of resultsToProcess) {
      if (newMetadata[result.url]) continue;

      try {
        // ACTION: The URL is now encoded and passed as a query parameter to the new proxy.
        const response = await fetch(`${CORS_PROXY}${encodeURIComponent(result.url)}`);
        if (!response.ok) continue;

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const favicon = doc.querySelector('link[rel="shortcut icon"]')?.href || 
                        doc.querySelector('link[rel="icon"]')?.href;
        
        const metaDescription = doc.querySelector('meta[name="description"]')?.content;
        
        // --- Snippet Generation Logic ---
        let description = metaDescription || 'No description available.';
        const bodyText = doc.body.innerText || '';
        const searchTerms = currentQuery.toLowerCase().split(/\s+/).filter(Boolean);
        let bestSnippet = '';

        if (bodyText && searchTerms.length > 0) {
            const lowerBodyText = bodyText.toLowerCase();
            let firstMatchIndex = -1;

            // Find the first occurrence of any search term
            for (const term of searchTerms) {
                const index = lowerBodyText.indexOf(term);
                if (index !== -1) {
                    firstMatchIndex = index;
                    break;
                }
            }

            if (firstMatchIndex !== -1) {
                const start = Math.max(0, firstMatchIndex - 80);
                const end = Math.min(bodyText.length, firstMatchIndex + 170);
                bestSnippet = (start > 0 ? '... ' : '') + bodyText.substring(start, end) + (end < bodyText.length ? ' ...' : '');
            }
        }
        
        if (bestSnippet) {
            description = bestSnippet;
        }

        newMetadata[result.url] = {
          favicon: favicon ? new URL(favicon, result.url).href : null,
          description: description,
        };
      } catch (err) {
        newMetadata[result.url] = {
          favicon: null,
          description: 'Could not fetch page summary.',
        };
        console.error(`Failed to fetch metadata for ${result.url}:`, err);
      }
    }
    setMetadata(newMetadata);
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    setResults([]);
    setMetadata({});

    try {
      const searchUrl = `${API_URL}?q=${encodeURIComponent(query)}`;
      const response = await fetch(searchUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResults(data.results || []);

    } catch (err) {
      console.error("Search API call failed:", err);
      setError('Failed to fetch search results. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
    setError(null);
    setMetadata({});
    searchInputRef.current.focus();
  };

  // --- Render Logic ---
  return (
    <div className={`App ${hasSearched ? 'results-mode' : 'home-mode'}`}>
      <header className="App-header">
        <div className="logo-container" onClick={handleClearSearch} title="Clear Search">
          <img src={ACEIQ_LOGO_URL} alt="AceIQ Logo" className="logo-image" />
          <h1>AceIQ Search</h1>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <input
              type="text"
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the web..."
              className="search-input"
              disabled={isLoading}
            />
            {query && (
              <button
                type="button"
                className="clear-button"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                &times;
              </button>
            )}
          </div>
          <button type="submit" className="search-button" disabled={isLoading}>
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </form>
      </header>

      <main className="results-container">
        {isLoading && <p className="status-message">Searching for results...</p>}
        {error && <p className="status-message error-message">{error}</p>}
        
        {hasSearched && !isLoading && !error && (
          <>
            {results.length > 0 ? (
              <ul className="results-list">
                {results.map((result, index) => {
                  const resultMeta = metadata[result.url];
                  return (
                    <li key={index} className="result-item">
                      <a href={result.url} target="_blank" rel="noopener noreferrer" className="result-link">
                        <div className="result-header">
                          <img 
                            src={resultMeta?.favicon || ACEIQ_LOGO_URL} 
                            alt="" 
                            className="result-favicon"
                            onError={(e) => { e.target.onerror = null; e.target.src=ACEIQ_LOGO_URL; }}
                          />
                          <div className="result-url-wrapper">
                            <span className="result-title">{result.title || 'No Title'}</span>
                            <span className="result-url">{result.url}</span>
                          </div>
                          {/* ACTION: Re-added the relevance score */}
                          <span className="result-score">Relevance: {result.score}</span>
                        </div>
                        {resultMeta && (
                          <p className="result-description">
                            {/* ACTION: Use the Snippet component for smart highlighting */}
                            <Snippet text={resultMeta.description} query={query} />
                          </p>
                        )}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="status-message">No results found for "{query}". Try another search.</p>
            )}
          </>
        )}
      </main>
      
      <footer className="App-footer">
        <p>
          <a href="https://aceiq.ca/" target="_blank" rel="noopener noreferrer">AceIQ</a> Search by Saksham Goel
        </p>
      </footer>
    </div>
  );
}

export default App;
